import type { ServicePresentation } from "~/content/service-presentations"

export const solutionPresentations = {
  "early-startups": {
    slug: "early-startups",
    label: "EARLY-STAGE STARTUPS",
    lead: "Get your first product",
    headline: "into people’s hands.",
    headlineAccent: "people’s hands",
    body: "You know the problem you want to solve. We help you choose a useful first version, design it, and build something your customers can try.",
    outcome: "Make the first\nversion count.",
    outcomeBody:
      "Get clear on what matters before you commit to a larger build. You bring the knowledge of your business. We bring the product and technical team.",
    outputs: [
      ["Choose what comes first", "Focus on the experience that makes the product useful."],
      ["Test the uncertain parts", "Try the assumptions that could change what you build."],
      ["Make it tangible", "Give customers and collaborators something concrete to react to."],
      ["Keep ownership", "Understand the decisions and keep the code as the product grows."],
    ],
    process: "From an idea\nto a first release.",
    processBody:
      "You work directly with the people shaping and building the product. Each round gives us a clearer next step.",
    steps: [
      [
        "Understand the need",
        "Start with your customers, their problem, and what you already know.",
      ],
      ["Shape the first version", "Map the important journeys and decide what can wait."],
      ["Build and try it", "Put working software in front of you and your early users."],
      ["Learn and improve", "Use what happens to decide what to change or build next."],
    ],
    artifactTitle: "A useful first product starts with a focused question.",
    artifact: ["Customer need", "First version", "User feedback", "Next priority"],
    proofName: "THE FOREM",
    proof: "From a pitch deck\nto a product people\ncould try.",
    proofBody:
      "We helped The Forem define its users, workflows, and core product concepts. An interactive prototype supported fundraising and product validation before the work moved into an MVP.",
    proofLink: "Read The Forem story →",
    start: "Start with\nwhat you have.",
    startBody:
      "You don’t need a finished specification or a technical co-founder to have a useful conversation.",
    entry: [
      ["An idea", "Work out the first useful version through Sprint Zero."],
      ["A prototype or designs", "Review what’s working and what still needs to be decided."],
      [
        "An app built with AI",
        "Find the gaps between the prototype and a product people can rely on.",
      ],
    ],
    bridge: "Sprint Zero helps define the build. A Pod gives you a team to keep moving.",
    close: "Let’s work out\nyour first version.",
    closeBody: "Tell us about your idea, what you’ve tried, and where you need help.",
    proofId: "the-forem",
  },
  "growth-startups": {
    slug: "growth-startups",
    label: "GROWTH-STAGE STARTUPS",
    lead: "Keep your roadmap moving.",
    headline: "Build what comes next.",
    headlineAccent: "what comes next",
    body: "Put an experienced product team on the next priority. We can own an initiative, improve an existing product, or build alongside your internal team.",
    outcome: "Put a team on\nthe next priority.",
    outcomeBody:
      "Some projects need focused attention. We help define the work, take responsibility for delivery, and stay connected to the people who depend on it.",
    outputs: [
      ["Customer-facing features", "Build the next useful addition to your product."],
      ["Tools and integrations", "Connect systems and make important workflows easier."],
      ["Focused AI experiments", "Test a specific opportunity before expanding it."],
      ["Product improvements", "Work through the parts that have become slow or difficult to use."],
    ],
    process: "Work alongside\nyour team.",
    processBody: "Clear ownership and regular reviews keep the work connected to your roadmap.",
    steps: [
      ["Agree on the outcome", "Choose the initiative and define a useful first release."],
      ["Map the dependencies", "Understand your systems, constraints, and team responsibilities."],
      ["Build in your workflow", "Keep decisions and progress visible in the tools you use."],
      ["Release and keep learning", "Review the result together and plan the next improvement."],
    ],
    artifactTitle: "One initiative, with clear ownership from start to finish.",
    artifact: ["Outcome", "Dependencies", "Working release", "Next improvement"],
    proofName: "DAPPER LABS",
    proof: "Help developers\nbuild on Flow.",
    proofBody:
      "We worked with Dapper on a testnet faucet, the KittyItems reference application, and a developer wallet. Each gave developers a more practical way to work with the platform.",
    proofLink: "Read the Dapper Labs story →",
    start: "Start with\none initiative.",
    startBody: "Bring the work that needs attention and the people it needs to connect with.",
    entry: [
      ["A new capability", "Define the release that would make a difference for customers."],
      [
        "An existing product",
        "Improve a specific experience or work through technical constraints.",
      ],
      ["A team that needs capacity", "Give us a clear area to own alongside your internal team."],
    ],
    bridge:
      "Mosaic Foods: a new meal-plan experience within existing systems. obé Fitness: an upgraded streaming product.",
    close: "What’s waiting\nto get built?",
    closeBody: "Tell us about the next priority and where your team could use help.",
    proofId: "dapper-labs",
  },
  "small-medium-businesses": {
    slug: "small-medium-businesses",
    label: "SMALL + MEDIUM BUSINESSES",
    lead: "Make your business",
    headline: "easier to run.",
    headlineAccent: "easier to run",
    body: "Bring us the process that takes too much work, the systems that don’t talk, or the product you want to offer your customers. We’ll help you build a useful improvement.",
    outcome: "Start with the work\nthat takes too much work.",
    outcomeBody:
      "Software should fit the way your business operates. We start with the people doing the work and the places where it gets difficult.",
    outputs: [
      [
        "Less repeated admin",
        "Connect steps that currently depend on copying, chasing, or re-entering information.",
      ],
      ["Easier customer journeys", "Make it simpler to book, pay, renew, or get help."],
      ["Connected operations", "Bring the information your team needs into a useful workflow."],
      [
        "A product from your expertise",
        "Turn something you know well into a service people can use.",
      ],
    ],
    process: "Build around\nhow you work.",
    processBody:
      "Start with one practical improvement, try it with the people who use it, and build from there.",
    steps: [
      ["Walk through the work", "See what staff and customers do today."],
      ["Choose the improvement", "Agree on the problem, the scope, and what better looks like."],
      ["Connect and test", "Build around existing systems and try the result together."],
      ["Keep improving", "Adjust the workflow as the business changes."],
    ],
    artifactTitle: "From a daily frustration to a working improvement.",
    artifact: ["Current workflow", "Useful change", "Try it together", "Keep improving"],
    proofName: "PEARLAND BROKERAGE",
    proof: "Renew a policy.\nMake a payment.\nSkip the office visit.",
    proofBody:
      "We built Pearland’s online platform for policy renewals, payments, and staff workflows, then kept improving it as the business and its systems changed.",
    proofLink: "Read the Pearland story →",
    start: "Different businesses.\nPractical software.",
    startBody: "The right project starts with how your business creates value for customers.",
    entry: [
      [
        "Pilates Pro Flow",
        "Class-planning software for Pilates instructors. A product built around specialist knowledge.",
      ],
      ["Chill-N", "Real-time equipment monitoring across ice cream locations."],
      ["Pearland Brokerage", "Customer self-service connected to the team’s daily work."],
    ],
    bridge: "A focused project can solve the first problem. A Pod can keep improvements moving.",
    close: "What would make\nthe day easier?",
    closeBody: "Tell us about the work, the systems, and the improvement you have in mind.",
    proofId: "pearland",
  },
  "private-equity": {
    slug: "private-equity",
    label: "PRIVATE EQUITY + INVESTMENT FIRMS",
    lead: "Move technology",
    headline: "forward across your portfolio.",
    headlineAccent: "your portfolio",
    body: "Get practical help assessing systems, improving customer experiences, and delivering technology projects across your businesses. Start with one company or a shared priority.",
    outcome: "Understand the systems.\nMove the work forward.",
    outcomeBody:
      "We connect the assessment to the work that follows, with priorities your operating teams can act on.",
    outputs: [
      [
        "Understand the systems",
        "Identify technical constraints, dependencies, and opportunities.",
      ],
      [
        "Improve the customer experience",
        "Make websites, portals, and digital services easier to use.",
      ],
      ["Connect operations", "Reduce friction between systems and teams."],
      [
        "Support portfolio teams",
        "Bring product, design, and engineering capacity where it’s needed.",
      ],
    ],
    process: "Shared foundations.\nRoom for each business.",
    processBody:
      "Look for work that can be reused while keeping each company’s customers and operations in view.",
    steps: [
      ["Start with the business", "Understand the company, the systems, and the priority."],
      [
        "Make the decisions visible",
        "Set out options, trade-offs, and a practical sequence of work.",
      ],
      ["Deliver with the operators", "Build and review with the people who run the business."],
      ["Apply what we learn", "Carry useful patterns into the next company or initiative."],
    ],
    artifactTitle: "A practical path from assessment to delivery.",
    artifact: ["Assess", "Prioritize", "Build", "Apply across teams"],
    proofName: "QUADRANT PRIVATE CAPITAL",
    proof: "Bring portfolio projections\nout of spreadsheets.",
    proofBody:
      "We built a secure client portal that moved financial calculations and projections into a cloud dashboard, with client visibility and integration with existing systems.",
    proofLink: "Read the Quadrant story →",
    start: "Work across companies.\nKeep the purpose clear.",
    startBody: "Different engagements can support a shared portfolio strategy.",
    entry: [
      ["Wagway", "Digital experiences for a portfolio of pet-care brands and locations."],
      [
        "Fullsteam",
        "Website work with the group and its businesses, including tools marketing teams can manage.",
      ],
      [
        "Quadrant Private Capital",
        "A client portal for financial projections, portfolio visibility, and repeatable workflows.",
      ],
    ],
    bridge: "Begin with diligence, a focused project, or an ongoing product team.",
    close: "What needs to move\nacross your portfolio?",
    closeBody: "Tell us about the company or shared initiative that needs attention.",
    proofId: "quadrant",
  },
  "accelerators": {
    slug: "accelerators",
    label: "ACCELERATORS + INCUBATORS",
    lead: "Give founders a team",
    headline: "they can turn to.",
    headlineAccent: "turn to",
    body: "Bring practical product and technical support into your program. From office hours to hands-on building, we help founders make decisions and take the next step.",
    outcome: "Help that meets founders\nwhere they are.",
    outcomeBody:
      "Give your cohort access to people who can work through a question, teach a skill, or help build the product.",
    outputs: [
      ["Office hours", "Product scope, architecture, hiring questions, and AI prototypes."],
      [
        "Workshops + hackathons",
        "Hands-on sessions where founders build, learn, and choose a next step.",
      ],
      ["Help when a team gets stuck", "Focused product, design, or engineering support."],
      ["Continued building", "Sprint Zero or a Pod for companies that need a delivery team."],
    ],
    process: "Build support\naround your program.",
    processBody:
      "Choose a rhythm that fits the cohort and a clear path for founders who need more help.",
    steps: [
      [
        "Understand the cohort",
        "Look at the founders, their products, and the questions they face.",
      ],
      ["Choose the format", "Combine office hours, workshops, hackathons, and focused support."],
      ["Work with the founders", "Make progress on actual product questions and builds."],
      ["Keep the next step clear", "Help teams continue independently or with a delivery team."],
    ],
    artifactTitle: "Practical support before, during, and after the program.",
    artifact: ["Office hours", "Hands-on sessions", "Focused support", "Continued building"],
    proofName: "OUR ROOTS · Q LABS",
    proof: "We started\ninside an incubator.",
    proofBody:
      "Eric and Dave met at Q Labs, an innovation lab inside AOL Ventures. The team launched seven products in fourteen months, working across product, design, engineering, and launch.",
    proofLink: "Meet Eric and Dave →",
    start: "Experience with founders\nfrom leading programs.",
    startBody: "We’ve worked with companies from ERA, Techstars, and Y Combinator.",
    entry: [
      ["Program-wide support", "Office hours and hands-on sessions available to the cohort."],
      ["Focused founder support", "Time with a team to work through a specific product question."],
      [
        "A path beyond the program",
        "Continued advice or delivery support as the company develops.",
      ],
    ],
    bridge:
      "Support can be arranged with the program, with individual companies, or through a combination of both.",
    close: "What would help\nyour next cohort?",
    closeBody: "Tell us about your program and the support your founders need.",
    proofId: "the-forem",
  },
} satisfies Record<string, ServicePresentation>
